import { Copy } from "lucide-react";
import { type SyntheticEvent, useState } from "react";
import type { WindowAction } from "../../web/components/RecordWindow";
import { ErrorNotice } from "../../web/components/ui";
import { WindowPopup } from "../../web/components/WindowPopup";
import { ITEM_NUMBER_MAX, type PriceListData } from "./meta";
import { itemNumberKey } from "./setup";

/*
 * Duplicating a record (its window's More menu): a popup in the window asking what has to differ,
 * its name (as it is, to change) and, for items and bundles, an item number (unique, so blank to
 * start), and for an item whether its supplier prices come too. Create makes the copy and opens it.
 */

/** Duplicate in a window's More menu: it opens the popup (`onOpen`), which asks the rest. */
export const duplicateAction = (kind: string, onOpen: () => void): WindowAction => ({
    icon: <Copy className="size-4" />,
    label: "Duplicate",
    help: `A new ${kind} like this one, with its own name${kind === "group" ? "" : " and item number"}.`,
    run: onOpen,
});

/** Another item or bundle already has this item number (they're unique across both). */
const numberTaken = (data: Pick<PriceListData, "materials" | "bundles">, number: string) => {
    const key = itemNumberKey(number);
    return [...data.materials, ...data.bundles].some((r) => r.active && itemNumberKey(r.item_number) === key);
};

export function DuplicatePopup(
    props: Readonly<{
        data: PriceListData;
        /** "item", "bundle", "group". */
        kind: string;
        name: string;
        /** It needs an item number (items and bundles). */
        withNumber: boolean;
        /** An item's supplier prices can come too (ticked to start); null: not offered. */
        supplierPrices: number | null;
        onCreate: (copy: { name: string; itemNumber: string; supplierPrices: boolean }) => Promise<unknown>;
        onClose: () => void;
    }>,
) {
    const [name, setName] = useState(props.name);
    const [number, setNumber] = useState("");
    const [withPrices, setWithPrices] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const numberProblem = (() => {
        if (!props.withNumber) return null;
        if (!number.trim()) return "Give it an item number (each is unique).";
        if (number.trim().length > ITEM_NUMBER_MAX) return `At most ${ITEM_NUMBER_MAX} characters.`;
        return numberTaken(props.data, number) ? "Another item or bundle has this item number." : null;
    })();
    const ready = name.trim() !== "" && numberProblem === null;
    const submit = (e: SyntheticEvent) => {
        e.preventDefault();
        if (!ready) return;
        setBusy(true);
        setError(null);
        props
            .onCreate({ name: name.trim(), itemNumber: number.trim(), supplierPrices: props.supplierPrices !== null && withPrices })
            .then(props.onClose)
            .catch((err: Error) => {
                setError(err);
                setBusy(false);
            });
    };
    return (
        <WindowPopup title={`Duplicate this ${props.kind}`} onClose={props.onClose}>
            <form className="space-y-4" onSubmit={submit}>
                <label className="block space-y-1">
                    <span className="field-label">Name</span>
                    <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                {props.withNumber ? (
                    <label className="block space-y-1">
                        <span className="field-label">Item number</span>
                        <input className="input" value={number} aria-invalid={number.trim() !== "" && numberProblem !== null} autoFocus onChange={(e) => setNumber(e.target.value)} />
                        {/* Said once it's typed (blank to start isn't a mistake yet). */}
                        {number.trim() && numberProblem ? <span className="block text-(length:--label-text) text-danger">{numberProblem}</span> : null}
                    </label>
                ) : null}
                {props.supplierPrices ? (
                    <label className="flex cursor-pointer items-center gap-2">
                        <input type="checkbox" checked={withPrices} onChange={(e) => setWithPrices(e.target.checked)} />
                        <span>
                            Copy its {props.supplierPrices === 1 ? "supplier price" : `${props.supplierPrices} supplier prices`}
                            <span className="text-muted"> (their codes and links too: check them for the new one)</span>
                        </span>
                    </label>
                ) : null}
                <ErrorNotice error={error} />
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-secondary" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={busy || !ready}>
                        <Copy className="size-4" /> Create copy
                    </button>
                </div>
            </form>
        </WindowPopup>
    );
}
